import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FEATURES, hasFeatureAccess } from "../helpers/featureAccess";
import { fetchFromApi } from "../helpers/fetchFromApi";
import {
  exportRows,
  exportSummary,
  exportSections,
  filterRows,
  marketLabels,
} from "../helpers/tipphilfeView";
import LeagueChoice from "./LeagueChoice";
import "./Tipphilfe.css";
export async function loadTipphilfe(signal) {
  const r = await fetchFromApi("tipphilfe/next-rounds?version=2", {
    method: "GET",
    signal,
  });
  const data = await r.json();
  if (!r.ok || data.error) throw Error(data.error || `HTTP ${r.status}`);
  if (data.export_format_version !== 2 || !Array.isArray(data.leagues))
    throw Error("INVALID_V2_EXPORT");
  return data;
}
export default function Tipphilfe(props) {
  return hasFeatureAccess(FEATURES.TIPPHILFE, props.user) ? (
    <PublishedTips {...props} />
  ) : null;
}
function PublishedTips({ load = loadTipphilfe }) {
  const { i18n } = useTranslation(),
    en = i18n.language?.startsWith("en"),
    t = (de, eng) => (en ? eng : de);
  const [data, setData] = useState(null),
    [error, setError] = useState(""),
    [filters, setFilters] = useState({
      league: "",
      market: "",
      section: "",
      search: "",
      sort: "date",
    });
  useEffect(() => {
    const c = new AbortController();
    load(c.signal)
      .then((d) => {
        if (!c.signal.aborted) setData(d);
      })
      .catch((e) => {
        if (!c.signal.aborted) setError(e.message);
      });
    return () => c.abort();
  }, [load]);
  const rows = useMemo(() => exportRows(data), [data]),
    visible = useMemo(() => filterRows(rows, filters), [rows, filters]),
    summary = useMemo(() => exportSummary(data), [data]);
  const change = (key, value) => setFilters((f) => ({ ...f, [key]: value }));
  const fmt = (v, pct = false) =>
    Number.isFinite(v)
      ? (v * (pct ? 100 : 1)).toLocaleString(en ? "en-GB" : "de-DE", {
          maximumFractionDigits: 2,
        }) + (pct ? " %" : "")
      : "—";
  const time = (v) => {
    const d = new Date(v);
    return Number.isFinite(d.getTime())
      ? d.toLocaleString(en ? "en-GB" : "de-DE", { timeZone: "Europe/Berlin" })
      : "—";
  };
  const team = (x) => (
    <span className="th-team">
      {(x?.image_path || x?.image) && (
        <img
          className="bs-crest"
          src={x.image_path || x.image}
          alt=""
          onError={(e) => {
            e.currentTarget.style.display = "none";
          }}
        />
      )}
      {x?.name || "—"}
    </span>
  );
  const sections = exportSections(data);
  return (
    <section className="tipphilfe-view" data-testid="tipphilfe">
      <h2>Tipphilfe</h2>
      <p>
        {t(
          "Veröffentlichter v2-Export für Marvin · nur lesend",
          "Published v2 export for Marvin · read only"
        )}
      </p>
      {error && (
        <p role="alert">
          {t("Export nicht verfügbar", "Export unavailable")}: {error}
        </p>
      )}
      {!data && !error && (
        <p role="status">{t("Export wird geladen …", "Loading export …")}</p>
      )}
      {data && (
        <>
          <div className="th-summary">
            <span>Run: {data.run_id || "—"}</span>
            <span>
              {t("Erstellt", "Created")}: {time(data.generated_at)} ·
              Europe/Berlin
            </span>
            <span>
              {t("Aktuell / folgend", "Current / next")}:{" "}
              {sections.join(" / ") || "—"}
            </span>
            <span>
              {summary.leagues} {t("Ligen", "leagues")} · {summary.fixtures}{" "}
              Fixtures · {summary.tips} {t("Tipps", "tips")}
            </span>
            <span>
              Reuse: {summary.reused} · {t("Neu berechnet", "Computed")}:{" "}
              {summary.computed} · {t("Problematisch", "Problematic")}:{" "}
              {summary.problematic}
            </span>
          </div>
          {summary.unknown > 0 && (
            <p role="status">
              {summary.unknown} Fixtures:{" "}
              {t(
                "Compilation-Audit fehlt im veröffentlichten Run.",
                "Compilation audit unavailable in the published run."
              )}
            </p>
          )}
          {data.partial && (
            <p role="status">
              {t(
                "Der Export meldet Datenlücken. Details stehen unter Diagnose.",
                "The export reports missing data. See diagnostics."
              )}
            </p>
          )}
          <div className="th-filters">
            <LeagueChoice
              leagues={data.leagues.map((l) => ({
                id: l.league_id,
                name: l.league_name,
              }))}
              value={filters.league}
              onChange={(v) => change("league", String(v))}
              label={t("Liga", "League")}
              placeholder={t("Alle Ligen", "All leagues")}
            />
            <button onClick={() => change("league", "")}>
              {t("Alle Ligen", "All leagues")}
            </button>
            <label>
              {t("Wochenabschnitt", "Week section")}
              <select
                aria-label={t("Wochenabschnitt", "Week section")}
                value={filters.section}
                onChange={(e) => change("section", e.target.value)}
              >
                <option value="">{t("Alle", "All")}</option>
                {[
                  ...new Set([
                    ...sections,
                    ...rows.map((r) => r.section).filter(Boolean),
                  ]),
                ].map((s) => (
                  <option key={s}>{s}</option>
                ))}
              </select>
            </label>
            <label>
              {t("Markt", "Market")}
              <select
                aria-label={t("Markt", "Market")}
                value={filters.market}
                onChange={(e) => change("market", e.target.value)}
              >
                <option value="">{t("Alle Märkte", "All markets")}</option>
                {[
                  ...new Set([
                    ...Object.keys(marketLabels),
                    ...rows.map((r) => r.group),
                  ]),
                ].map((m) => (
                  <option value={m} key={m}>
                    {marketLabels[m] || m}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Team
              <input
                aria-label="Team"
                value={filters.search}
                onChange={(e) => change("search", e.target.value)}
              />
            </label>
            <label>
              {t("Sortierung", "Sort")}
              <select
                aria-label={t("Sortierung", "Sort")}
                value={filters.sort}
                onChange={(e) => change("sort", e.target.value)}
              >
                <option value="date">
                  {t("Datum / Uhrzeit", "Date / time")}
                </option>
                <option value="value">Value ↓</option>
                <option value="league">{t("Liga", "League")}</option>
              </select>
            </label>
          </div>
          <div className="th-quick">
            {["", "1x2", "over-under-2.5"].map((m) => (
              <button
                key={m}
                aria-pressed={filters.market === m}
                onClick={() => change("market", m)}
              >
                {marketLabels[m] || t("Alle Märkte", "All markets")}
              </button>
            ))}
          </div>
          <p>
            {visible.length} / {summary.tips} {t("Tipps", "tips")}
          </p>
          <div className="bs-table-scroll">
            <table>
              <thead>
                <tr>
                  {[
                    t("Datum / Uhrzeit", "Date / time"),
                    t("Liga", "League"),
                    t("Paarung", "Fixture"),
                    t("Markt / Auswahl", "Market / selection"),
                    t("Marktquote", "Market odds"),
                    t("Wahrscheinlichkeit", "Probability"),
                    t("Faire Quote", "Fair odds"),
                    "Value",
                    "xG H / A",
                  ].map((x) => (
                    <th key={x}>{x}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visible.map((r) => (
                  <tr key={r.id}>
                    <td>{time(r.fixture.kickoff)}</td>
                    <td>{r.league.league_name}</td>
                    <td>
                      {team(r.fixture.home_team)} – {team(r.fixture.away_team)}
                    </td>
                    <td>
                      {marketLabels[r.group] || r.group} · {r.selection}
                      {r.line != null ? ` (${r.line})` : ""}
                    </td>
                    <td>{fmt(r.quote)}</td>
                    <td>{fmt(r.probability, true)}</td>
                    <td>{fmt(r.fair)}</td>
                    <td>{fmt(r.value)}</td>
                    <td>
                      {fmt(r.fixture.model?.goal_expectancies?.home)} /{" "}
                      {fmt(r.fixture.model?.goal_expectancies?.away)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!visible.length && (
            <p>
              {t(
                "Keine Tipps für diese Auswahl vorhanden.",
                "No tips available for this selection."
              )}
            </p>
          )}
          <p>
            {t(
              "— = im Export nicht vorhanden. Value wird unverändert aus dem Export angezeigt; keine zusätzliche Berechnung.",
              "— = unavailable in export. Value is shown unchanged; no additional calculation."
            )}
          </p>
          <details>
            <summary>{t("Diagnose", "Diagnostics")}</summary>
            {data.leagues.map((l) => (
              <div key={l.league_id}>
                <strong>{l.league_name}</strong>
                <ul>
                  {(l.issues || []).map((v, i) => (
                    <li key={i}>
                      {typeof v === "string" ? v : JSON.stringify(v)}
                    </li>
                  ))}
                  {(l.fixtures || [])
                    .filter((f) => f.compilation_audit?.decision === "problem")
                    .map((f) => (
                      <li key={f.fixture_id}>
                        {f.home_team?.name} – {f.away_team?.name}:{" "}
                        {f.compilation_audit.reason}
                      </li>
                    ))}
                </ul>
              </div>
            ))}
          </details>
        </>
      )}
    </section>
  );
}
